import { useTranslation } from 'react-i18next';
import { PublicRoutes } from 'src/routes/navigation';
import {
  Container_v2 as Container,
  Typography,
  TypographyVariantsEnum,
  Button_v2 as Button,
  IconGoogle,
  IconApple,
  Loading,
  OnboardingVariant,
  SolidInput,
  ProgressBar_v2 as ProgressBar,
  newShade,
  ModalSheet,
} from '@mindly/ui-components';
import { useAnalytics, useAuthFlow, validateEmail } from 'src/shared';
import { KeyboardEventHandler, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './Auth.module.css';
import { isPlatform } from '@ionic/react';
import { SupportedLangsType } from 'src/shared/enums/supportedLangs.enum';
import { selectTermsOfConditions } from 'src/redux/firestoreGlobalVariables/selector';
import { setAuthAction, setIsLinkUserExist, setSignUpEmail } from 'src/redux/auth/reducer';
import { useHistory } from 'react-router';
import { onboardingDataSelector, signUpEmailSelector } from 'src/redux/auth/selector';
import { AuthPageHeaderFeature } from 'src/features/AuthPageHeaderFeature';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import DefaultAuthWidget from './DefaultAuthWidget';
import useToast from 'src/context/ToastContext/ToastContext';

type WebToWebAuthWidgetProps = {
  isLoading: boolean;
  isLinkUserExist: boolean;
  authType: 'apple' | 'google' | null;
  handleSignIn: () => void;
  handleGoogleSignIn: () => void;
  handleAppleSignIn: () => void;
  handleLinkClick: (url: string) => void;
  setIsOnboardingDataLoading: (isLoading: boolean) => void;
};

const WebToWebAuthWidget = ({
  isLoading,
  isLinkUserExist,
  authType,
  handleSignIn,
  handleGoogleSignIn,
  handleAppleSignIn,
  handleLinkClick,
  setIsOnboardingDataLoading,
}: WebToWebAuthWidgetProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation('auth');
  const { trackPredefinedEvent } = useAnalytics();
  const { triggerSignInWithCustomTokenFlow, isEmailExist, authUser } = useAuthFlow();
  const authExperiment = useFeatureIsOn('funnel-auth-page');
  const { showToast } = useToast();

  const email = useSelector(signUpEmailSelector);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);

  const termsConditions = useSelector(selectTermsOfConditions);
  const onboardingData = useSelector(onboardingDataSelector);

  const termsOfConditionLink = useMemo(() => {
    const currentLocale = i18n.language as SupportedLangsType | 'en';

    return termsConditions?.[currentLocale] ?? termsConditions?.en;
  }, [i18n.language, termsConditions]);

  const onboardingVariant = useMemo(() => {
    return onboardingData?.theme_v2?.template ?? onboardingData?.theme ?? OnboardingVariant.default;
  }, [onboardingData?.theme_v2?.template]);

  const handleEmailSubmit = async () => {
    if (isDisabled) return;

    try {
      setIsEmailLoading(true);

      if (authUser) {
        const token = localStorage.getItem('onboardingToken');
        if (token) {
          await triggerSignInWithCustomTokenFlow(token);
        }
      }

      const isEmailExistResult = await isEmailExist(email);

      if (typeof isEmailExistResult !== 'boolean') {
        return;
      }

      dispatch(setAuthAction(isEmailExistResult ? 'signIn' : authUser ? 'link' : 'signUp'));

      trackPredefinedEvent('SIGNUP_USER_INFO_SUBMITTED', {
        screen_type: 'email',
        value: email,
        is_login: isEmailExistResult,
      });

      if (isEmailExistResult) {
        history.push(PublicRoutes.PASSWORD);
      } else {
        history.push(PublicRoutes.NAME_SIGN_UP);
      }
    } catch (error) {
      showToast({ error });
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' && email) {
      handleEmailSubmit();
    }
  };

  useEffect(() => {
    setIsDisabled(!validateEmail(email));
  }, [email]);

  const isCustomOnboardingTheme = useMemo(() => {
    return onboardingVariant === OnboardingVariant.custom;
  }, [onboardingVariant]);

  const customThemeButtonStyles = useMemo(
    () => ({
      '--background': onboardingData?.theme_v2?.customColor ?? '',
      '--background-focused': onboardingData?.theme_v2?.customColor ?? '',
      '--background-hover': onboardingData?.theme_v2?.customColor ?? '',
      '--background-activated': newShade(onboardingData?.theme_v2?.customColor ?? '', 10),
      '--color': onboardingData?.theme_v2?.customTextColor ?? '',
    }),
    [onboardingData?.theme_v2?.customColor, onboardingData?.theme_v2?.customTextColor]
  );

  const customThemeProgressBarStyles = useMemo(
    () => ({
      '--progress-background': onboardingData?.theme_v2?.customColor ?? '',
    }),
    [onboardingData?.theme_v2?.customColor]
  );

  if (!authExperiment) {
    return (
      <DefaultAuthWidget
        isLinkUserExist={isLinkUserExist}
        authType={authType}
        handleSignIn={handleSignIn}
        isLoading={isLoading}
        handleGoogleSignIn={handleGoogleSignIn}
        handleAppleSignIn={handleAppleSignIn}
        handleLinkClick={handleLinkClick}
        setIsOnboardingDataLoading={setIsOnboardingDataLoading}
      />
    );
  }

  return (
    <>
      <AuthPageHeaderFeature setIsOnboardingDataLoading={setIsOnboardingDataLoading}>
        <div className="flex items-center justify-center">
          <div className="w-[70%] mt-4">
            <ProgressBar
              progress={0.95}
              variant={onboardingVariant === OnboardingVariant.blue ? 'blue' : 'dark'}
              style={isCustomOnboardingTheme ? customThemeProgressBarStyles : {}}
            />
          </div>
        </div>
      </AuthPageHeaderFeature>

      <Container className={styles.container}>
        <div className={'flex flex-col flex-1 items-center justify-between'}>
          <div>
            <Typography className="mb-5" variant={TypographyVariantsEnum.Title24Semi}>
              {t('auth.auth.webToWebAuth.title')}
            </Typography>

            <SolidInput
              placeholder={t('auth.auth.webToWebAuth.emailPlaceholder')}
              value={email}
              onChange={(e) => dispatch(setSignUpEmail(e.target.value?.toString() ?? ''))}
              type="email"
              name="email"
              onKeyDown={handleKeyDown}
              itemClassName="rounded-[24px]"
            />
            <Button
              onClick={handleEmailSubmit}
              fullWidth
              size={'large'}
              className="!mt-4"
              disabled={isDisabled}
              isLoading={isEmailLoading}
              variant={onboardingVariant}
              style={isCustomOnboardingTheme ? customThemeButtonStyles : undefined}
            >
              {t('auth.auth.webToWebAuth.continueButton')}
            </Button>

            <div className="w-full flex justify-between items-center my-4">
              <div className="w-full h-[1px] bg-borders-divider" />
              <Typography
                className="text-typography-typography-secondary mx-[14px]"
                variant={TypographyVariantsEnum.Text13Regular}
              >
                {t('auth.auth.webToWebAuth.dividerText')}
              </Typography>
              <div className="w-full h-[1px] bg-borders-divider" />
            </div>

            <div className={'flex flex-col gap-4 w-full'}>
              <Button
                fullWidth
                fill={'outline'}
                size={'large'}
                icon={<IconGoogle />}
                iconPosition={'start'}
                variant={isCustomOnboardingTheme ? OnboardingVariant.blue : onboardingVariant}
                onClick={handleGoogleSignIn}
              >
                {t('auth.auth.google.button')}
              </Button>
              {!isPlatform('android') && (
                <Button
                  fullWidth
                  fill={'outline'}
                  size={'large'}
                  icon={<IconApple />}
                  iconPosition={'start'}
                  variant={isCustomOnboardingTheme ? OnboardingVariant.blue : onboardingVariant}
                  onClick={handleAppleSignIn}
                >
                  {t('auth.auth.apple.button')}
                </Button>
              )}
            </div>
          </div>
          <Typography variant={TypographyVariantsEnum.Text13Regular} align={'center'} className="my-3" opacity={50}>
            {t('auth.auth.terms.text')}
            <span
              onClick={() => handleLinkClick(`https://mindlyspace.com/${i18n.language}/privacy-policy`)}
              className={'text-xm underline cursor-pointer'}
            >
              {t('auth.auth.terms.link.0')}
            </span>{' '}
            {t('auth.auth.terms.and')}{' '}
            <span onClick={() => handleLinkClick(termsOfConditionLink)} className={'text-xm underline cursor-pointer'}>
              {t('auth.auth.terms.link.2')}
            </span>
          </Typography>
        </div>
        <Loading isOpen={isLoading} />
      </Container>
      <ModalSheet
        isOpen={isLinkUserExist}
        heightInPx={290}
        onIonModalDidDismiss={() => dispatch(setIsLinkUserExist(false))}
      >
        <div className={'h-full pt-8 px-5'}>
          <Typography variant={TypographyVariantsEnum.Text24Semi} align={'center'} className={'mb-3'}>
            {t('auth.login.google.title')}
          </Typography>
          <Typography variant={TypographyVariantsEnum.Text14Regular} align={'center'} opacity={50} className={'mb-8'}>
            {t('auth.login.google.description')}
          </Typography>
          <Button
            fullWidth
            fill={'outline'}
            size={'large'}
            icon={authType === 'apple' ? <IconApple /> : <IconGoogle />}
            iconPosition={'start'}
            onClick={handleSignIn}
          >
            {authType === 'apple' ? t('auth.login.apple.button') : t('auth.login.google.button')}
          </Button>
        </div>
      </ModalSheet>
    </>
  );
};

export default WebToWebAuthWidget;
